
import { BrowserRouter as Router, Route, Routes, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { lazy, Suspense } from "react";
import "./App.css";

// Loading component
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
  </div>
);

// Error boundary component
const ErrorFallback = () => (
  <div className="min-h-screen flex items-center justify-center flex-col gap-4">
    <p className="text-red-600">Something went wrong loading the page.</p>
    <button 
      onClick={() => window.location.reload()}
      className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
    >
      Retry
    </button>
  </div>
);

// Protected Route component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const isAuthenticated = localStorage.getItem('sb-pjkzxpunlergxoqdokwv-auth-token');
  
  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
};

// Lazy load pages with error handling
const lazyLoad = (importFn: () => Promise<any>) => {
  return lazy(async () => {
    try {
      return await importFn();
    } catch (error) {
      console.error("Error loading component:", error);
      return { default: ErrorFallback };
    }
  });
};

// Lazy load pages
const Home = lazyLoad(() => import("@/pages/Index"));
const Dashboard = lazyLoad(() => import("@/pages/Dashboard"));
const Auth = lazyLoad(() => import("@/pages/Auth"));
const MySpace = lazyLoad(() => import("@/pages/MySpace"));
const EditProfile = lazyLoad(() => import("@/pages/EditProfile"));
const Pricing = lazyLoad(() => import("@/pages/Pricing"));
const PostPayment = lazyLoad(() => import("@/pages/PostPayment"));
const ListingDetails = lazyLoad(() => import("@/pages/ListingDetail"));
const AddListing = lazyLoad(() => import("@/pages/AddListing"));
const EditListing = lazyLoad(() => import("@/pages/EditListing"));
const AdminPanel = lazyLoad(() => import("@/pages/AdminPanel"));

function App() {
  return (
    <Router>
      <Toaster />
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } 
          />
          <Route path="/auth" element={<Auth />} />
          <Route path="/edit-profile" element={<EditProfile />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/post-payment" element={<PostPayment />} />
          <Route path="/listing/:id" element={<ListingDetails />} />
          <Route path="/add-listing" element={<AddListing />} />
          <Route path="/create-listing" element={<AddListing />} />
          <Route path="/edit-listing/:id" element={<EditListing />} />
          <Route path="/my-space" element={<MySpace />} />
          <Route path="/admin" element={<AdminPanel />} />
          <Route path="/:username" element={<MySpace isPublic={true} />} />
        </Routes>
      </Suspense>
    </Router>
  );
}

export default App;
